import type { Environment, EnvironmentData, MfeVersion, Snapshot } from "./types";

export const SNAPSHOT_STORAGE_KEY = "hawaii-dashboard-snapshots";

// Legacy: old snapshots may contain hawaii:shell/router entries from when
// the API mixed them into the MFE list. Keep filtering for backwards compat.
const IGNORED_MFE_NAMES = new Set(["hawaii:router", "hawaii:shell"]);

const SNAPSHOT_ENVIRONMENT_ORIGINS: Record<Environment, string> = {
  dev: "https://platform.dev.accuris.dev/",
  stage: "https://platform.stage.accuris.dev/",
  prod: "https://platform.accuristech.com/",
};

export function filterIgnoredMfes<T>(mfes: Record<string, T>): Record<string, T> {
  return Object.fromEntries(
    Object.entries(mfes).filter(([name]) => !IGNORED_MFE_NAMES.has(name)),
  );
}

function sanitizeSnapshot(snapshot: Snapshot): Snapshot {
  return {
    ...snapshot,
    versions: filterIgnoredMfes(snapshot.versions),
  };
}

export function loadSnapshots(): Snapshot[] {
  try {
    const raw = localStorage.getItem(SNAPSHOT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Snapshot[]).map((snapshot) => sanitizeSnapshot(snapshot)) : [];
  } catch {
    return [];
  }
}

export function persistSnapshots(snapshots: Snapshot[]) {
  localStorage.setItem(
    SNAPSHOT_STORAGE_KEY,
    JSON.stringify(snapshots.map((snapshot) => sanitizeSnapshot(snapshot))),
  );
}

export function computeSnapshotDiff(
  snapshotVersions: Record<string, MfeVersion>,
  currentMfes: Record<string, MfeVersion>,
): string[] {
  const differing: string[] = [];

  for (const [name, snap] of Object.entries(snapshotVersions)) {
    const current = currentMfes[name];
    if (current && current.resolved !== snap.resolved) {
      differing.push(name);
    }
  }

  return differing;
}

export function buildDefaultSnapshotName(timestamp: Date): string {
  const year = timestamp.getUTCFullYear();
  const month = String(timestamp.getUTCMonth() + 1).padStart(2, "0");
  const day = String(timestamp.getUTCDate()).padStart(2, "0");
  const hours = String(timestamp.getUTCHours()).padStart(2, "0");
  const minutes = String(timestamp.getUTCMinutes()).padStart(2, "0");
  const seconds = String(timestamp.getUTCSeconds()).padStart(2, "0");

  return `snapshot ${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function getSnapshotTargetEnvironment(environment: Environment): "stage" | "prod" | null {
  if (environment === "dev") return "stage";
  if (environment === "stage") return "prod";
  return null;
}

export function getSnapshotEnvironmentOrigin(environment: Environment): string | null {
  return SNAPSHOT_ENVIRONMENT_ORIGINS[environment] ?? null;
}

export function getSnapshotEnvironmentUrl(environment: Environment): string | null {
  const origin = getSnapshotEnvironmentOrigin(environment);
  if (!origin) {
    return null;
  }

  return new URL("thread", origin).toString();
}

export function buildSnapshotOverrideToken(name: string, version: MfeVersion): string {
  return `${name}_${version.resolved}`;
}

export function getSnapshotOverrideTokens(
  snapshotVersions: Record<string, MfeVersion>,
  currentEnvironment?: EnvironmentData | null,
): string[] {
  const sortedEntries = Object.entries(filterIgnoredMfes(snapshotVersions)).sort(([left], [right]) => left.localeCompare(right));
  const allTokens = sortedEntries.map(([name, version]) => buildSnapshotOverrideToken(name, version));

  if (!currentEnvironment) {
    return allTokens;
  }

  const changedTokens: string[] = [];

  for (const [name, version] of sortedEntries) {
    const currentVersion = currentEnvironment.mfes[name];
    if (!currentVersion) {
      continue;
    }

    if (currentVersion.resolved !== version.resolved) {
      changedTokens.push(buildSnapshotOverrideToken(name, version));
    }
  }

  return changedTokens;
}

export function getSortedSnapshotVersions(snapshot: Snapshot): Array<[string, MfeVersion]> {
  return Object.entries(filterIgnoredMfes(snapshot.versions)).sort(([left], [right]) => left.localeCompare(right));
}